/**
 * device/apps/settings.ts — Settings App
 *
 * A clean iOS-style settings panel for the in-game device. Expandable
 * sections for sound, device color (with full 36-color picker), and
 * notifications.
 *
 * Tapping "Device Color" expands an inline color picker with group tabs,
 * swatch grid, and phone/pad selector — the player can change their device
 * color at any time, not just during character creation.
 */

import type { GameState } from "../../engine/state.js";
import { setDeviceContext } from "../../character/composer.js";
import {
  type DeviceApp,
  type Rect,
  drawRoundRect,
  drawText,
  registerApp,
} from "../app.js";

// ─── Device Colors (shared with character_create) ────────────────────────

interface DeviceColorEntry {
  hex: string;
  name: string;
  group: string;
}

const DEVICE_COLORS: DeviceColorEntry[] = [
  // Classics
  { hex: "#3a3a3a", name: "Space Gray",    group: "Classic" },
  { hex: "#c0c0c0", name: "Silver",        group: "Classic" },
  { hex: "#f5f0e8", name: "Starlight",     group: "Classic" },
  { hex: "#1c1c2e", name: "Midnight",      group: "Classic" },
  { hex: "#2a2a2a", name: "Obsidian",      group: "Classic" },
  { hex: "#e0e0e0", name: "Pearl",         group: "Classic" },
  // Earth
  { hex: "#5b3a29", name: "Espresso",      group: "Earth" },
  { hex: "#c49056", name: "Amber",         group: "Earth" },
  { hex: "#e8c8a0", name: "Sand",          group: "Earth" },
  { hex: "#8a6e4a", name: "Walnut",        group: "Earth" },
  { hex: "#d4a07a", name: "Terracotta",    group: "Earth" },
  { hex: "#f0dcc0", name: "Cream",         group: "Earth" },
  // Nature
  { hex: "#2a4a3a", name: "Forest",        group: "Nature" },
  { hex: "#c0d0a0", name: "Sage",          group: "Nature" },
  { hex: "#4a6a50", name: "Moss",          group: "Nature" },
  { hex: "#90b090", name: "Fern",          group: "Nature" },
  { hex: "#1a3a2a", name: "Pine",          group: "Nature" },
  { hex: "#d0e0c0", name: "Mint",          group: "Nature" },
  // Sky & Water
  { hex: "#1a3a5a", name: "Ocean",         group: "Sky" },
  { hex: "#a0c8e0", name: "Sky",           group: "Sky" },
  { hex: "#2a4a6a", name: "Denim",         group: "Sky" },
  { hex: "#6090b0", name: "Steel Blue",    group: "Sky" },
  { hex: "#c0d8e8", name: "Ice",           group: "Sky" },
  { hex: "#1a2a4a", name: "Navy",          group: "Sky" },
  // Warm
  { hex: "#8b1a1a", name: "Crimson",       group: "Warm" },
  { hex: "#f0a0b0", name: "Rose",          group: "Warm" },
  { hex: "#c04040", name: "Cherry",        group: "Warm" },
  { hex: "#e07050", name: "Coral",         group: "Warm" },
  { hex: "#f0c0c0", name: "Blush",         group: "Warm" },
  { hex: "#d06030", name: "Ember",         group: "Warm" },
  // Bold
  { hex: "#4a2a5a", name: "Violet",        group: "Bold" },
  { hex: "#6a3a7a", name: "Amethyst",      group: "Bold" },
  { hex: "#c080d0", name: "Lavender",      group: "Bold" },
  { hex: "#e0b020", name: "Gold",          group: "Bold" },
  { hex: "#f0d060", name: "Sunflower",     group: "Bold" },
  { hex: "#20c0a0", name: "Teal",          group: "Bold" },
];

const COLOR_GROUPS = [...new Set(DEVICE_COLORS.map(c => c.group))];

// ─── Per-App State ──────────────────────────────────────────────────────

interface SettingsState {
  soundOn: boolean;
  soundLevel: number; // 0–1
  notificationsOn: boolean;
  colorPickerOpen: boolean;
  colorPickerDevice: "phone" | "pad";
  colorGroup: string;
  scrollY: number;
  scrollVelocity: number;
  maxScrollY: number;
}

function getState(state: GameState): SettingsState {
  if (!state.player.device.appStates["settings"]) {
    state.player.device.appStates["settings"] = {
      soundOn: true,
      soundLevel: 0.75,
      notificationsOn: true,
      colorPickerOpen: false,
      colorPickerDevice: "phone",
      colorGroup: "Classic",
      scrollY: 0,
      scrollVelocity: 0,
      maxScrollY: 0,
    } satisfies SettingsState;
  }
  return state.player.device.appStates["settings"] as SettingsState;
}

// ─── Layout Constants ───────────────────────────────────────────────────

const ROW_HEIGHT = 28;
const ROW_PAD = 8;
const LABEL_FONT = "10px sans-serif";
const VALUE_FONT = "9px sans-serif";
const SMALL_FONT = "7px sans-serif";
const ROW_BG = "#2a2018";
const SEPARATOR = "#3a2e20";
const LABEL_COLOR = "#f5e6d0";
const VALUE_COLOR = "rgba(245,230,208,0.55)";
const ACCENT = "#e8a050";
const GREEN = "#6ab060";
const RED = "#d05040";

// ─── Icon Drawing ───────────────────────────────────────────────────────

function drawGearIcon(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  size: number,
): void {
  const r = size * 0.3;
  const outerR = size * 0.44;
  const teeth = 8;

  ctx.save();
  ctx.beginPath();

  for (let i = 0; i < teeth; i++) {
    const baseAngle = (i / teeth) * Math.PI * 2;
    const halfTooth = (0.3 / teeth) * Math.PI * 2;

    const a0 = baseAngle - halfTooth;
    const a1 = baseAngle + halfTooth;
    const a2 = a1;
    const a3 = ((i + 1) / teeth) * Math.PI * 2 - halfTooth;

    if (i === 0) {
      ctx.moveTo(cx + Math.cos(a0) * outerR, cy + Math.sin(a0) * outerR);
    } else {
      ctx.lineTo(cx + Math.cos(a0) * outerR, cy + Math.sin(a0) * outerR);
    }
    ctx.lineTo(cx + Math.cos(a1) * outerR, cy + Math.sin(a1) * outerR);
    ctx.lineTo(cx + Math.cos(a2) * r, cy + Math.sin(a2) * r);
    ctx.lineTo(cx + Math.cos(a3) * r, cy + Math.sin(a3) * r);
  }

  ctx.closePath();
  ctx.fillStyle = "#d1d1d6";
  ctx.fill();

  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.5, 0, Math.PI * 2);
  ctx.fillStyle = "#636366";
  ctx.fill();

  ctx.restore();
}

// ─── Helper: draw a setting row ─────────────────────────────────────────

function drawRow(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  yOffset: number,
  label: string,
  drawValue: (x: number, y: number, w: number, h: number) => void,
  expandable = false,
  expanded = false,
): number {
  const y = rect.y + yOffset;
  const rowW = rect.w;

  drawRoundRect(ctx, rect.x, y, rowW, ROW_HEIGHT, 0, ROW_BG);

  // Label
  drawText(ctx, label, rect.x + ROW_PAD, y + 9, rowW * 0.5, LABEL_FONT, LABEL_COLOR, "left");

  // Expand chevron for expandable rows
  if (expandable) {
    const chevX = rect.x + rowW - ROW_PAD - 4;
    const chevY = y + ROW_HEIGHT / 2;
    ctx.save();
    ctx.strokeStyle = VALUE_COLOR;
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    if (expanded) {
      ctx.moveTo(chevX - 3, chevY - 2);
      ctx.lineTo(chevX, chevY + 2);
      ctx.lineTo(chevX + 3, chevY - 2);
    } else {
      ctx.moveTo(chevX - 2, chevY - 3);
      ctx.lineTo(chevX + 2, chevY);
      ctx.lineTo(chevX - 2, chevY + 3);
    }
    ctx.stroke();
    ctx.restore();
  }

  // Value area
  const valueX = rect.x + rowW * 0.38;
  const valueW = rowW * 0.62 - ROW_PAD - (expandable ? 12 : 0);
  drawValue(valueX, y, valueW, ROW_HEIGHT);

  // Separator
  ctx.fillStyle = SEPARATOR;
  ctx.fillRect(rect.x + ROW_PAD, y + ROW_HEIGHT - 1, rowW - ROW_PAD * 2, 0.5);

  return ROW_HEIGHT;
}

// ─── Color Picker Panel ─────────────────────────────────────────────────

function drawColorPicker(
  ctx: CanvasRenderingContext2D,
  rect: Rect,
  yOffset: number,
  state: GameState,
  s: SettingsState,
): number {
  const x0 = rect.x;
  const w = rect.w;
  let y = rect.y + yOffset;
  const startY = y;

  // Background
  drawRoundRect(ctx, x0, y, w, 4, 0, "#111113");

  // ── Phone / Pad selector ──────────────────────────────────────────
  const tabW = w / 2 - 4;
  const tabH = 22;
  const tabY = y + 4;

  // Phone tab
  const phoneSel = s.colorPickerDevice === "phone";
  drawRoundRect(ctx, x0 + 2, tabY, tabW, tabH, 6,
    phoneSel ? ACCENT : "#1a1410");
  drawText(ctx, "hPhone", x0 + 2 + tabW / 2, tabY + 6, tabW, VALUE_FONT,
    phoneSel ? "#fff" : VALUE_COLOR, "center");

  // Current phone color swatch in tab
  const phSwY = tabY + 4;
  const phSwX = x0 + 2 + tabW - 18;
  drawRoundRect(ctx, phSwX, phSwY, 14, 14, 3, state.player.device.hPhone.color, "#555");

  // Pad tab
  const padSel = s.colorPickerDevice === "pad";
  drawRoundRect(ctx, x0 + 2 + tabW + 4, tabY, tabW, tabH, 6,
    padSel ? ACCENT : "#1a1410");
  drawText(ctx, "hPad", x0 + 2 + tabW + 4 + tabW / 2, tabY + 6, tabW, VALUE_FONT,
    padSel ? "#fff" : VALUE_COLOR, "center");

  // Current pad color swatch in tab
  const pdSwX = x0 + 2 + tabW + 4 + tabW - 18;
  drawRoundRect(ctx, pdSwX, phSwY, 14, 14, 3, state.player.device.hPad.color, "#555");

  y += tabH + 8;

  // ── Group tabs (Classic, Earth, Nature, Sky, Warm, Bold) ──────────
  const groupBtnW = Math.floor((w - 4) / COLOR_GROUPS.length);
  const groupBtnH = 16;
  for (let i = 0; i < COLOR_GROUPS.length; i++) {
    const gx = x0 + 2 + i * groupBtnW;
    const active = s.colorGroup === COLOR_GROUPS[i];
    drawRoundRect(ctx, gx, y, groupBtnW - 2, groupBtnH, 4,
      active ? "#3a2a18" : "#1a1410");
    drawText(ctx, COLOR_GROUPS[i].slice(0, 5), gx + (groupBtnW - 2) / 2, y + 4,
      groupBtnW - 4, SMALL_FONT, active ? LABEL_COLOR : VALUE_COLOR, "center");
  }
  y += groupBtnH + 4;

  // ── Swatch grid ───────────────────────────────────────────────────
  const filtered = DEVICE_COLORS.filter(c => c.group === s.colorGroup);
  const swatchSize = 18;
  const swatchGap = 4;
  const cols = Math.floor((w - 4) / (swatchSize + swatchGap));
  const activeColor = s.colorPickerDevice === "phone"
    ? state.player.device.hPhone.color
    : state.player.device.hPad.color;

  for (let i = 0; i < filtered.length; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const sx = x0 + 4 + col * (swatchSize + swatchGap);
    const sy = y + row * (swatchSize + swatchGap);

    // Selection ring
    if (filtered[i].hex === activeColor) {
      drawRoundRect(ctx, sx - 2, sy - 2, swatchSize + 4, swatchSize + 4, 5, undefined, ACCENT);
      ctx.strokeStyle = ACCENT;
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Swatch
    drawRoundRect(ctx, sx, sy, swatchSize, swatchSize, 4, filtered[i].hex, "#444");
  }

  const rows = Math.ceil(filtered.length / cols);
  y += rows * (swatchSize + swatchGap) + 2;

  // ── Color name label ──────────────────────────────────────────────
  const currentEntry = DEVICE_COLORS.find(c => c.hex === activeColor);
  if (currentEntry) {
    drawText(ctx, currentEntry.name, x0 + w / 2, y, w - 8, VALUE_FONT, VALUE_COLOR, "center");
    y += 14;
  }

  // Fill background to full height
  const totalH = y - startY;
  ctx.save();
  ctx.globalCompositeOperation = "destination-over";
  drawRoundRect(ctx, x0, startY, w, totalH, 0, "#111113");
  ctx.restore();

  return totalH;
}

// ─── Color Picker Tap Handling ──────────────────────────────────────────

function handleColorPickerTap(
  localX: number,
  localY: number,
  rect: Rect,
  yOffset: number,
  state: GameState,
  s: SettingsState,
): boolean {
  const x0 = rect.x;
  const w = rect.w;
  let y = yOffset;

  // ── Phone / Pad tabs ──────────────────────────────────────────────
  const tabW = w / 2 - 4;
  const tabH = 22;
  const tabY = y + 4;

  if (localY >= tabY && localY < tabY + tabH) {
    if (localX >= x0 + 2 && localX < x0 + 2 + tabW) {
      s.colorPickerDevice = "phone";
      return true;
    }
    if (localX >= x0 + 2 + tabW + 4 && localX < x0 + 2 + tabW * 2 + 4) {
      s.colorPickerDevice = "pad";
      return true;
    }
  }
  y += tabH + 8;

  // ── Group tabs ────────────────────────────────────────────────────
  const groupBtnW = Math.floor((w - 4) / COLOR_GROUPS.length);
  const groupBtnH = 16;
  if (localY >= y && localY < y + groupBtnH) {
    for (let i = 0; i < COLOR_GROUPS.length; i++) {
      const gx = x0 + 2 + i * groupBtnW;
      if (localX >= gx && localX < gx + groupBtnW - 2) {
        s.colorGroup = COLOR_GROUPS[i];
        return true;
      }
    }
  }
  y += groupBtnH + 4;

  // ── Swatch grid ───────────────────────────────────────────────────
  const filtered = DEVICE_COLORS.filter(c => c.group === s.colorGroup);
  const swatchSize = 18;
  const swatchGap = 4;
  const cols = Math.floor((w - 4) / (swatchSize + swatchGap));

  for (let i = 0; i < filtered.length; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const sx = x0 + 4 + col * (swatchSize + swatchGap);
    const sy = y + row * (swatchSize + swatchGap);

    if (localX >= sx && localX < sx + swatchSize &&
        localY >= sy && localY < sy + swatchSize) {
      // Apply the color!
      const newColor = filtered[i].hex;
      if (s.colorPickerDevice === "phone") {
        state.player.device.hPhone.color = newColor;
      } else {
        state.player.device.hPad.color = newColor;
      }
      // Update composer context so the sprite reflects the new color immediately
      const dev = state.player.device;
      if (dev.activeDevice) {
        const color = dev.activeDevice === "pad" ? dev.hPad.color : dev.hPhone.color;
        setDeviceContext(color, dev.activeDevice);
        // Character re-composites on next frame via room renderer
      }
      return true;
    }
  }

  return false;
}

// ─── The App ────────────────────────────────────────────────────────────

const settingsApp: DeviceApp = {
  id: "settings",
  name: "Settings",
  iconGradient: ["#b89870", "#8a6a48"],

  drawIcon(ctx, cx, cy, size) {
    drawGearIcon(ctx, cx, cy, size);
  },

  drawContent(ctx, rect, state, dt) {
    const s = getState(state);

    // ── Momentum / inertia scroll ────────────────────────────────────
    if (Math.abs(s.scrollVelocity) > 0.5) {
      s.scrollY += s.scrollVelocity * dt;
      s.scrollVelocity *= Math.exp(-6 * dt);
    } else {
      s.scrollVelocity = 0;
    }
    s.scrollY = Math.max(0, Math.min(s.scrollY, s.maxScrollY));

    let yOff = 0;

    // ── Row 0: Sound ──────────────────────────────────────────────
    yOff += drawRow(ctx, rect, yOff, "Sound", (vx, vy, vw, vh) => {
      const barX = vx + 4;
      const barY = vy + vh / 2 - 4;
      const barW = vw - 24;
      const barH = 8;

      drawRoundRect(ctx, barX, barY, barW, barH, 4, "#38383a");

      if (s.soundOn && s.soundLevel > 0) {
        const fillW = Math.max(4, barW * s.soundLevel);
        drawRoundRect(ctx, barX, barY, fillW, barH, 4, GREEN);
      }

      const labelText = s.soundOn ? "ON" : "OFF";
      const labelColor = s.soundOn ? GREEN : RED;
      drawText(ctx, labelText, vx + vw - 4, vy + 10, 24, VALUE_FONT, labelColor, "right");
    });

    // ── Row 1: Device Color (expandable) ──────────────────────────
    yOff += drawRow(ctx, rect, yOff, "Device Color", (vx, vy, vw, _vh) => {
      // Show current swatches inline
      const swatchSize = 12;
      const swatchY = vy + ROW_HEIGHT / 2 - swatchSize / 2;
      const phoneColor = state.player.device.hPhone.color;
      const padColor = state.player.device.hPad.color;

      const sx1 = vx + vw - swatchSize * 2 - 14;
      drawRoundRect(ctx, sx1, swatchY, swatchSize, swatchSize, 3, phoneColor, "#555");

      const sx2 = vx + vw - swatchSize - 8;
      drawRoundRect(ctx, sx2, swatchY, swatchSize, swatchSize, 3, padColor, "#555");

      drawText(ctx, "Ph", sx1 + swatchSize / 2, vy + 4, 20, SMALL_FONT, VALUE_COLOR, "center");
      drawText(ctx, "Pd", sx2 + swatchSize / 2, vy + 4, 24, SMALL_FONT, VALUE_COLOR, "center");
    }, true, s.colorPickerOpen);

    // ── Expanded color picker ─────────────────────────────────────
    if (s.colorPickerOpen) {
      yOff += drawColorPicker(ctx, rect, yOff, state, s);
    }

    // ── Row 2: Notifications ──────────────────────────────────────
    yOff += drawRow(ctx, rect, yOff, "Notifications", (vx, vy, vw, vh) => {
      const toggleW = 28;
      const toggleH = 14;
      const tx = vx + vw - toggleW - 2;
      const ty = vy + vh / 2 - toggleH / 2;

      const trackColor = s.notificationsOn ? GREEN : "#48484a";
      drawRoundRect(ctx, tx, ty, toggleW, toggleH, toggleH / 2, trackColor);

      const knobR = toggleH / 2 - 2;
      const knobCx = s.notificationsOn ? tx + toggleW - knobR - 3 : tx + knobR + 3;
      const knobCy = ty + toggleH / 2;
      ctx.beginPath();
      ctx.arc(knobCx, knobCy, knobR, 0, Math.PI * 2);
      ctx.fillStyle = "#f5e6d0";
      ctx.fill();
    });

    // ── Row 3: Controls ───────────────────────────────────────────
    yOff += drawRow(ctx, rect, yOff, "Controls", (vx, vy, vw, _vh) => {
      drawText(ctx, "D=Phone  ⇧D=Pad  I=Bag",
        vx + vw - 2, vy + 6, vw, VALUE_FONT, VALUE_COLOR, "right");
      drawText(ctx, "R=Rotate  A=Aeterna  ↵=Chat",
        vx + vw - 2, vy + 17, vw, VALUE_FONT, VALUE_COLOR, "right");
    });

    // ── Update max scroll ──────────────────────────────────────────
    s.maxScrollY = Math.max(0, yOff - rect.h);
    s.scrollY = Math.max(0, Math.min(s.scrollY, s.maxScrollY));
  },

  // ── Drag scroll ──────────────────────────────────────────────────────

  handleDrag(dy, state) {
    const s = getState(state);
    s.scrollY = Math.max(0, Math.min(s.scrollY + dy, s.maxScrollY));
    s.scrollVelocity = 0;
  },

  handleDragEnd(velocityY, state) {
    const s = getState(state);
    s.scrollVelocity = velocityY;
  },

  handleTap(localX, localY, state) {
    const s = getState(state);
    let yOff = 0;

    // Row 0: Sound toggle
    if (localY >= yOff && localY < yOff + ROW_HEIGHT) {
      s.soundOn = !s.soundOn;
      return true;
    }
    yOff += ROW_HEIGHT;

    // Row 1: Device Color — toggle picker
    if (localY >= yOff && localY < yOff + ROW_HEIGHT) {
      s.colorPickerOpen = !s.colorPickerOpen;
      return true;
    }
    yOff += ROW_HEIGHT;

    // Color picker panel (if expanded)
    if (s.colorPickerOpen) {
      // Calculate picker height to check bounds
      const filtered = DEVICE_COLORS.filter(c => c.group === s.colorGroup);
      const swatchSize = 18;
      const swatchGap = 4;
      const contentRect = state.player.device.appContentRect;
      const w = contentRect ? contentRect.w : 172;
      const cols = Math.max(1, Math.floor((w - 4) / (swatchSize + swatchGap)));
      const rows = Math.ceil(filtered.length / cols);
      const pickerH = 22 + 8 + 16 + 4 + rows * (swatchSize + swatchGap) + 16;

      if (localY >= yOff && localY < yOff + pickerH) {
        return handleColorPickerTap(localX, localY, { x: 0, y: 0, w, h: 0 }, yOff, state, s);
      }
      yOff += pickerH;
    }

    // Row 2: Notifications toggle
    if (localY >= yOff && localY < yOff + ROW_HEIGHT) {
      s.notificationsOn = !s.notificationsOn;
      return true;
    }

    return false;
  },

  badge(_state) {
    return 0;
  },
};

// ─── Self-Register ──────────────────────────────────────────────────────

registerApp(settingsApp);
